/**
 * useIncidentLocations Hook
 *
 * React Query hook for fetching active VMS incidents with coordinates
 * for display as map markers on the dispatch map.
 * Polls every 30 seconds as a fallback; socket events provide real-time updates.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { getIncidentsForMap } from '../../../../../api/canvms/incidents';
import type { VmsIncidentMapData } from '../../../../CANvision/types/canvision.types';

/** Query key for incident locations */
export const INCIDENT_LOCATIONS_QUERY_KEY = ['incidentLocations'];

/** Polling interval (30 seconds — socket events handle real-time, polling is fallback) */
const REFETCH_INTERVAL = 30 * 1000;

/** Stale time (15 seconds) */
const STALE_TIME = 15 * 1000;

export interface UseIncidentLocationsOptions {
  /** Enable or disable the query (default: true) */
  enabled?: boolean;
  /** Override polling interval (ms) */
  refetchInterval?: number;
}

export interface UseIncidentLocationsResult {
  /** Active incidents with coordinates */
  incidents: VmsIncidentMapData[];
  /** Total incident count */
  count: number;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Manually trigger a refresh */
  refetch: () => void;
  /** Add a real-time incident to the cache (from socket event) */
  addIncident: (incident: VmsIncidentMapData) => void;
  /** Remove a resolved incident from the cache */
  removeIncident: (incidentId: number) => void;
}

/**
 * Hook to fetch active VMS incidents for the dispatch map
 */
export function useIncidentLocations(
  options: UseIncidentLocationsOptions = {}
): UseIncidentLocationsResult {
  const {
    enabled = true,
    refetchInterval = REFETCH_INTERVAL,
  } = options;

  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: INCIDENT_LOCATIONS_QUERY_KEY,
    queryFn: getIncidentsForMap,
    enabled,
    staleTime: STALE_TIME,
    refetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // Filter to only incidents with valid coordinates
  const incidents = useMemo(() => {
    if (!data) return [];
    return data.filter(
      (incident) =>
        incident.latitude != null &&
        incident.longitude != null &&
        incident.latitude !== 0 &&
        incident.longitude !== 0
    );
  }, [data]);

  // Add a new incident to the cache (from socket event)
  const addIncident = useCallback(
    (incident: VmsIncidentMapData) => {
      queryClient.setQueryData<VmsIncidentMapData[]>(
        INCIDENT_LOCATIONS_QUERY_KEY,
        (old) => {
          if (!old) return [incident];
          // Avoid duplicates
          const exists = old.some((i) => i.id === incident.id);
          if (exists) return old;
          return [incident, ...old];
        }
      );
    },
    [queryClient]
  );

  // Remove a resolved incident from the cache
  const removeIncident = useCallback(
    (incidentId: number) => {
      queryClient.setQueryData<VmsIncidentMapData[]>(
        INCIDENT_LOCATIONS_QUERY_KEY,
        (old) => {
          if (!old) return [];
          return old.filter((i) => i.id !== incidentId);
        }
      );
    },
    [queryClient]
  );

  return {
    incidents,
    count: incidents.length,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    refetch,
    addIncident,
    removeIncident,
  };
}

export default useIncidentLocations;
